import React from "react";
import { useModules } from "@/context/ModulesContext";
import { useAuth } from "@/context/AuthContext";
import { Wrench, Lock, ShieldAlert } from "lucide-react";

/**
 * Wraps a route. If the matching backend module is on `maintenance` or
 * `disabled`, renders a friendly screen with the admin's custom message
 * instead of the children.  Admins always see-through.
 */
export default function ModuleGuard({ name, children }) {
  const { getModule, loaded } = useModules();
  const { user } = useAuth();

  if (!loaded) return children; // don't block while we load — flicker-free

  const mod = getModule(name);
  if (!mod || mod.status === "active") return children;
  if (user?.role === "admin") return (
    <>
      <AdminBanner mod={mod} />
      {children}
    </>
  );

  if (mod.status === "maintenance") return <MaintenanceScreen mod={mod} />;
  if (mod.status === "disabled") return <DisabledScreen mod={mod} />;
  return children;
}

function AdminBanner({ mod }) {
  const color = mod.status === "disabled" ? "#FF3B6B" : "#F59E0B";
  return (
    <div
      data-testid={`module-admin-banner-${mod.name}`}
      className="sticky top-[68px] z-30 mx-auto max-w-7xl px-5 mt-3"
    >
      <div
        className="glass-strong rounded-2xl px-4 py-2.5 flex items-center gap-3 border"
        style={{ borderColor: `${color}55`, background: `${color}11` }}
      >
        <ShieldAlert size={16} style={{ color }} />
        <div className="text-xs sm:text-sm font-display font-bold uppercase tracking-wider" style={{ color }}>
          Admin-Vorschau · Modul {mod.label || mod.name} ist {mod.status === "disabled" ? "deaktiviert" : "im Wartungsmodus"}
        </div>
      </div>
    </div>
  );
}

function MaintenanceScreen({ mod }) {
  return (
    <div data-testid={`module-maintenance-${mod.name}`} className="container mx-auto max-w-3xl px-5 py-24 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 mb-6">
        <Wrench size={32} className="text-[#F59E0B]" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#F59E0B] mb-3">Wartungsmodus</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter silver-text mb-4">
        {mod.label || mod.name}
      </h1>
      <p className="text-white/70 font-body leading-relaxed max-w-xl mx-auto whitespace-pre-line">
        {mod.message || "Dieser Bereich wird gerade aktualisiert. Schau in Kürze wieder vorbei — wir machen ihn noch schneller, schöner und blauer."}
      </p>
      <div className="mt-8 inline-flex items-center gap-2 text-xs text-white/40">
        <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />
        Wir arbeiten dran.
      </div>
    </div>
  );
}

function DisabledScreen({ mod }) {
  return (
    <div data-testid={`module-disabled-${mod.name}`} className="container mx-auto max-w-3xl px-5 py-24 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FF3B6B]/15 border border-[#FF3B6B]/40 mb-6">
        <Lock size={32} className="text-[#FF3B6B]" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF3B6B] mb-3">Aktuell nicht verfügbar</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter silver-text mb-4">
        {mod.label || mod.name}
      </h1>
      <p className="text-white/70 font-body leading-relaxed max-w-xl mx-auto whitespace-pre-line">
        {mod.message || "Dieses Modul wurde von einem Administrator vorübergehend deaktiviert."}
      </p>
    </div>
  );
}
